// Lista de campanhas: tabela no computador, cartões no celular (sem rolagem horizontal).
import { Link, useNavigate } from 'react-router-dom'
import { num } from '@impulso/shared'
import type { CampaignListItem } from '@impulso/shared/api/insights'
import { HealthBadge, Platform } from '../../ui'
import { Sparkline } from '../../ui/charts'
import { money, money2, useIsNarrow } from '../insights/components'
import { CampaignStatusSwitch } from './CampaignStatusSwitch'

const sparkColor = (c: CampaignListItem) => (c.health === 'bad' ? '#dc2626' : c.health === 'attention' ? '#d97706' : '#5b3df5')

/** Versão completa (tela Campanhas). */
export function CampaignTable({ companyId, items, days }: { companyId: string; items: CampaignListItem[]; days: number }) {
  const narrow = useIsNarrow()
  const navigate = useNavigate()
  const to = (c: CampaignListItem) => `/app/${companyId}/campanhas/${c.campaignId}`
  if (narrow) {
    return (
      <div className="list">
        {items.map((c) => (
          <div className="list-item" key={c.campaignId}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Link to={to(c)} className="strong">{c.name}</Link>
              <div className="row wrap mt-8" style={{ gap: 8 }}>
                <Platform platform={c.provider} label={c.channelLabel} />
                <HealthBadge value={c.health} />
              </div>
              <div className="small muted mt-8">{c.healthReason}</div>
              <div className="row wrap mt-8 small" style={{ gap: 12 }}>
                <span>{money(c.spend)} em {days} dias</span>
                <span>{num(c.results)} {c.results === 1 ? c.resultLabel.one : c.resultLabel.many}</span>
                {c.costPerResult != null && <span className="strong">{money2(c.costPerResult)} cada</span>}
              </div>
            </div>
            <CampaignStatusSwitch companyId={companyId} campaign={c} />
          </div>
        ))}
      </div>
    )
  }
  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            <th style={{ width: 56 }}>Ligada</th>
            <th>Campanha</th>
            <th>Situação</th>
            <th className="right">Por dia</th>
            <th className="right">Investido ({days}d)</th>
            <th className="right">Resultados</th>
            <th className="right">Custo por resultado</th>
            <th>Tendência</th>
          </tr>
        </thead>
        <tbody>
          {items.map((c) => (
            <tr key={c.campaignId} className="clickable" onClick={() => navigate(to(c))}>
              <td onClick={(e) => e.stopPropagation()}><CampaignStatusSwitch companyId={companyId} campaign={c} /></td>
              <td style={{ minWidth: 220 }}>
                <Link to={to(c)} className="strong" onClick={(e) => e.stopPropagation()}>{c.name}</Link>
                <div className="row mt-8" style={{ gap: 10 }}>
                  <Platform platform={c.provider} label={c.channelLabel} />
                  <span className="small muted">· {c.objectiveLabel}</span>
                </div>
              </td>
              <td style={{ maxWidth: 260 }}>
                <HealthBadge value={c.health} />
                <div className="small muted mt-8">{c.healthReason}</div>
              </td>
              <td className="right nowrap">{money(c.dailyBudget)}</td>
              <td className="right nowrap">{money(c.spend)}</td>
              <td className="right nowrap">{num(c.results)} <span className="small muted">{c.results === 1 ? c.resultLabel.one : c.resultLabel.many}</span></td>
              <td className="right nowrap strong">{money2(c.costPerResult)}</td>
              <td><Sparkline values={c.trend} color={sparkColor(c)} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/** Versão resumida (início). */
export function CampaignSummaryTable({ companyId, items }: { companyId: string; items: CampaignListItem[] }) {
  const narrow = useIsNarrow()
  const to = (c: CampaignListItem) => `/app/${companyId}/campanhas/${c.campaignId}`
  if (narrow) {
    return (
      <div className="list">
        {items.map((c) => (
          <div className="list-item" key={c.campaignId}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Link to={to(c)} className="strong">{c.name}</Link>
              <div className="row wrap mt-8" style={{ gap: 8 }}><HealthBadge value={c.health} /></div>
            </div>
            <div className="right">
              <div className="strong">{num(c.results)}</div>
              <div className="small muted">{money(c.spend)}</div>
            </div>
          </div>
        ))}
      </div>
    )
  }
  return (
    <div className="table-wrap">
      <table className="table" style={{ marginTop: 12 }}>
        <thead>
          <tr><th>Campanha</th><th>Situação</th><th className="right">Investido</th><th className="right">Clientes</th><th>Tendência</th></tr>
        </thead>
        <tbody>
          {items.map((c) => (
            <tr key={c.campaignId}>
              <td>
                <Link to={to(c)} className="strong">{c.name}</Link>
                <div className="mt-8"><Platform platform={c.provider} label={c.channelLabel} /></div>
              </td>
              <td style={{ maxWidth: 280 }}>
                <HealthBadge value={c.health} />
                <div className="small muted mt-8">{c.healthReason}</div>
              </td>
              <td className="right nowrap">{money(c.spend)}</td>
              <td className="right">{num(c.results)}</td>
              <td><Sparkline values={c.trend} color={sparkColor(c)} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
